import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Box,
  IconButton,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Stack,
  CheckboxGroup,
  Select,
  Divider,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "react-toastify";
import { EditIcon } from "@chakra-ui/icons";
import { BASE_URL } from "../../Globalapi/Globalapi";

const DashboardCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventColor, setEventColor] = useState("");
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const schoolId = sessionStorage.getItem("school_id");

  const {
    isOpen: isDetailModalOpen,
    onOpen: onOpenDetailModal,
    onClose: onCloseDetailModal,
  } = useDisclosure();

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    event_category_id: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    attachment: null,
    message: "",
    user_types: [],
  });

  const [editFormData, setEditFormData] = useState(null);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const capitalizeWords = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/admin/events/event_master/list.php?school_id=${schoolId}`
        );
        const result = await response.json();
        if (result.success) {
          setCategories(result.data);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("Failed to fetch categories.");
      }
    };

    if (schoolId) fetchCategories();
  }, [schoolId]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/event/list_all_event.php?school_id=${schoolId}`
        );
        if (response.data.success) {
          setEvents(response.data.data.events);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };
    fetchEvents();
  }, []);

  const fetchTeachers = async () => {
    try {
      const jwt = sessionStorage.getItem("jwt");
      const response = await axios.get(
        `${BASE_URL}/admin/staff/staff_list.php`,
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      if (response.data.status === "success") {
        setTeachers(response.data.data);
      } else {
        setError(response.data.message || "Error fetching teachers");
      }
    } catch (error) {
      setError("Network Error: " + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleEventClick = async (event) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/events/event/view_event.php?event_id=${event.id}`
      );
      if (response.data.success) {
        const fetchedEvent = response.data.data.event;
        setFormData({
          id: fetchedEvent.id,
          title: fetchedEvent.event_title,
          event_category_id: fetchedEvent.event_master_id,
          start_date: fetchedEvent.start_date,
          end_date: fetchedEvent.end_date,
          start_time: fetchedEvent.start_time,
          end_time: fetchedEvent.end_time,
          message: fetchedEvent.message,
          user_types: fetchedEvent.user_types || [],
          attachment: fetchedEvent.attachment,
          teacher_id: fetchedEvent.teacher_id,
        });
        setEventColor(getRandomColor());
        onOpenDetailModal();
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
      toast.error("Failed to fetch event details");
    }
  };

  const handleSaveEditEvent = async () => {
    if (!editFormData) return;
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/events/event/edit_event.php`,
        {
          event_id: editFormData.id,
          event_title: editFormData.title,
          event_master_id: editFormData.event_category_id,
          start_date: editFormData.start_date,
          end_date: editFormData.end_date,
          start_time: editFormData.start_time,
          end_time: editFormData.end_time,
          teacher_id: editFormData.teacher_id,
          message: editFormData.message,
          user_types: editFormData.user_types,
          pass: sessionStorage.getItem("user_id"),
        }
      );

      if (response.data.success) {
        toast.success("Event updated successfully");
        setEvents(
          events.map((event) =>
            event.id === editFormData.id ? { ...event, ...editFormData } : event
          )
        );
        setIsEditModalOpen(false);
      } else {
        toast.error("Failed to update event");
      }
    } catch (error) {
      toast.error("Error updating event");
    }
  };

  const handleEditClick = () => {
    setEditFormData(formData);
    setIsEditModalOpen(true);
  };

  const getRandomColor = () => {
    const colors = [
      // "#E6F4FF",
      "#B2F7F0",
      // "#F6FFED",
      // "#FFFBE6",
      // "#FFF1F0",
      // "#F9F0FF",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const generateCalendar = () => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    let calendarDays = [];
    let dayNumber = 1;

    for (let i = 0; i < 6; i++) {
      calendarDays[i] = [];
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < firstDay) {
          calendarDays[i].push(null);
        } else if (dayNumber <= daysInMonth) {
          const currentDay = new Date(year, month, dayNumber);
          const isToday = today.toDateString() === currentDay.toDateString();
          const eventsForDay = events.filter(
            (event) =>
              format(new Date(event.start_date), "yyyy-MM-dd") ===
              format(currentDay, "yyyy-MM-dd")
          );
          calendarDays[i].push({
            day: dayNumber,
            isToday,
            events: eventsForDay,
          });
          dayNumber++;
        } else {
          calendarDays[i].push(null);
        }
      }
    }
    return calendarDays;
  };

  const calendarDays = generateCalendar();
  const userTypeMapping = {
    3: "Student",
    2: "Teacher",
    4: "Guardian",
  };

  return (
    <>
      <Box className="calendar-box" w="100%" maxW="600px" >
        <div className="calendar-container " >
          <div className="calendar" >
            <div className="headerr" >
              <button
                className="headr"
                onClick={() =>
                  setCurrentDate(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth() - 1
                    )
                  )
                }
              >
                &lt;
              </button>
              <h2>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                className="headr"
                onClick={() =>
                  setCurrentDate(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth() + 1
                    )
                  )
                }
              >
                &gt;
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>S</th>
                  <th>M</th>
                  <th>T</th>
                  <th>W</th>
                  <th>T</th>
                  <th>F</th>
                  <th>S</th>
                </tr>
              </thead>
              <tbody>
                {calendarDays.map((week, index) => (
                  <tr key={index}>
                    {week.map((day, dayIndex) =>
                      day ? (
                        <td
                          key={dayIndex}
                          className={`day ${day.isToday ? "today" : ""}`}
                        >
                          <div className="day-number">{day.day}</div>
                          {day.events.length > 0 && (
                            <div className="events-container">
                              {day.events
                                .slice(0, 2)
                                .map((event, eventIndex) => (
                                  <div
                                    key={eventIndex}
                                    className="event-dot"
                                    style={{
                                      backgroundColor: getRandomColor(),
                                    }}
                                    onClick={() => handleEventClick(event)}
                                    title={event.event_title}
                                  />
                                ))}
                              {day.events.length > 2 && (
                                <div className="more-events">
                                  +{day.events.length - 2}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      ) : (
                        <td key={dayIndex}></td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Box>

      {/* Event Detail Modal */}
      <Modal isOpen={isDetailModalOpen} onClose={onCloseDetailModal}>
        <ModalOverlay />
        <ModalContent maxW="40%" maxH="90vh" bg={eventColor}>
          <ModalHeader
            fontSize="md"
            fontWeight="bold"
            borderBottom="1px solid grey"
          >
            Event Details
            <IconButton
              icon={<EditIcon />}
              onClick={() => {
                onCloseDetailModal();
                handleEditClick();
              }}
              variant="outline"
              colorScheme="teal"
              aria-label="Edit Event"
              ml={2}
              size="sm"
            />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4} px={6}>
            {formData && (
              <>
                <Text fontSize="sm" mb={3}>
                  {capitalizeWords(formData.title)}
                </Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {capitalizeWords(formData.message)}
                </Text>
                <Flex justifyContent="space-between" mb={4}>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium">
                      Start: {formatDate(formData.start_date)}
                      {formData.start_time && ` at ${formData.start_time}`}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium">
                      End: {formatDate(formData.end_date)}
                      {formData.end_time && ` at ${formData.end_time}`}
                    </Text>
                  </Box>
                </Flex>
                {formData.user_types?.length > 0 && (
                  <Box mt={3}>
                    <Text fontSize="xs" fontWeight="medium" mb={1}>
                      Event For:
                    </Text>
                    <Flex gap={2} flexWrap="wrap">
                      {formData.user_types.map((id) => (
                        <Box
                          key={id}
                          px={2}
                          py={1}
                          bg="teal.50"
                          borderRadius="sm"
                        >
                          <Text fontSize="xs">{userTypeMapping[id]}</Text>
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader fontSize="md">Edit Event</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editFormData && (
              <Stack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Title</FormLabel>
                  <Input
                    size="sm"
                    value={editFormData.title}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        title: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Category</FormLabel>
                  <Select
                    size="sm"
                    value={editFormData.event_category_id}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        event_category_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.event_master}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Teacher</FormLabel>
                  <Select
                    size="sm"
                    value={editFormData.teacher_id}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        teacher_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <Flex gap={3}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">Start Date</FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={editFormData.start_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          start_date: e.target.value,
                        })
                      }
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">End Date</FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={editFormData.end_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          end_date: e.target.value,
                        })
                      }
                    />
                  </FormControl>
                </Flex>

                <Flex gap={3}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">Start Time</FormLabel>
                    <Input
                      size="sm"
                      type="time"
                      value={editFormData.start_time}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          start_time: e.target.value,
                        })
                      }
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">End Time</FormLabel>
                    <Input
                      size="sm"
                      type="time"
                      value={editFormData.end_time}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          end_time: e.target.value,
                        })
                      }
                    />
                  </FormControl>
                </Flex>

                <FormControl>
                  <FormLabel fontSize="sm">Message</FormLabel>
                  <Input
                    size="sm"
                    value={editFormData.message}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        message: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Event for</FormLabel>
                  <CheckboxGroup
                    colorScheme="teal"
                    value={editFormData.user_types.map(String)}
                    onChange={(selectedUserTypes) =>
                      setEditFormData({
                        ...editFormData,
                        user_types: selectedUserTypes.map(Number),
                      })
                    }
                  >
                    <Stack direction="row" spacing={4}>
                      <Checkbox value="3" size="sm">
                        Student
                      </Checkbox>
                      <Checkbox value="2" size="sm">
                        Teacher
                      </Checkbox>
                      <Checkbox value="4" size="sm">
                        Guardian
                      </Checkbox>
                    </Stack>
                  </CheckboxGroup>
                </FormControl>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={() => {
                handleSaveEditEvent();
                setIsEditModalOpen(false);
              }}
            >
              Save
            </Button>
            <Button size="sm" ml={2} onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <style>
        {`
          .calendar-container {
            margin: 0;
            padding: 42px 8px;
            background: white;
            // border-radius: 8px;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          }

          .calendar {
            width: 100%;
            min-width: auto;
          }

          .headerr {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px;
            margin-bottom: 8px;
          }

          .headerr h2 {
            font-size: 0.9rem;
            margin: 0;
            color: #333;
            font-weight: 500;
          }

          .headr {
            padding: 4px 8px;
            background: #f0f0f0;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.8rem;
            color: #666;
          }

          .headr:hover {
            background: #e0e0e0;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          table th {
            padding: 4px;
            font-size: 0.7rem;
            background: #f5f5f5;
            color: #666;
            text-align: center;
            font-weight: 500;
          }

          table td {
            padding: 2px;
            height: 36px;
            vertical-align: top;
            border: 1px solid #eee;
            font-size: 0.8rem;
          }

          .day-number {
            font-size: 0.75rem;
            margin: 2px;
            color: #333;
          }

          .events-container {
            display: flex;
            flex-wrap: wrap;
            gap: 2px;
            padding: 2px;
          }

          .event-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            cursor: pointer;
          }

          .more-events {
            font-size: 0.6rem;
            color: #666;
            text-align: center;
            margin-top: 2px;
          }

          .today {
            background-color: #f0f7ff;
          }

          .today .day-number {
            color: #1890ff;
            font-weight: 500;
          }
        `}
      </style>
    </>
  );
};

export default DashboardCalendar;
